import { css, useTheme } from '@emotion/react';
import { Menu } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useMatch, useSearchParams } from 'react-router-dom';

import { DashboardHeader } from '../components/dashboard/dashboard-header';
import { Sidebar } from '../components/layout/sidebar';
import { useToast } from '../components/ui/toast-context';
import { layout, mq, spacing } from '../theme/tokens';

const COLLAPSE_STORAGE_KEY = 'sonematic-sidebar-collapsed';

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true',
  );
  const theme = useTheme();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const isEditorRoute = useMatch('/app/projects/:id/edit');
  const isAdminRoute = useMatch('/app/admin/*');
  const isEditorContext = !!(isEditorRoute || isAdminRoute);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      addToast({
        variant: 'success',
        title: 'Subscription activated',
        message: 'Your plan has been upgraded successfully.',
      });
      setSearchParams({}, { replace: true });
    } else if (checkout === 'canceled') {
      addToast({
        variant: 'info',
        title: 'Checkout canceled',
        message: 'Your plan was not changed.',
      });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, addToast]);

  const handleSidebarClose = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const handleMobileMenuClick = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const sidebarWidth = collapsed ? layout.sidebarCollapsedWidth : layout.sidebarWidth;

  // Dashboard context: DashboardHeader + full-width content
  if (!isEditorContext) {
    return (
      <div
        css={css`
          min-height: 100vh;
          background: ${theme.colors.background};
          display: flex;
          flex-direction: column;
        `}
      >
        <DashboardHeader />
        <main
          css={css`
            flex: 1;
            min-height: 0;
          `}
        >
          <Outlet />
        </main>
      </div>
    );
  }

  // Editor context: sidebar + content grid
  return (
    <div
      css={css`
        min-height: 100vh;
        background: ${theme.colors.background};
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: 44px 1fr;

        ${mq.lg} {
          grid-template-columns: ${sidebarWidth} 1fr;
          grid-template-rows: 1fr;
          transition: grid-template-columns 0.2s ease;
        }
      `}
    >
      <Sidebar
        isOpen={sidebarOpen}
        onClose={handleSidebarClose}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
      />

      {/* Mobile top bar — hamburger only, hidden on desktop */}
      <div
        css={css`
          position: sticky;
          top: 0;
          height: 44px;
          background: ${theme.colors.sidebarBg};
          border-bottom: 1px solid ${theme.colors.border};
          display: flex;
          align-items: center;
          padding: 0 ${spacing.md};
          z-index: 30;

          ${mq.lg} {
            display: none;
          }
        `}
      >
        <button
          aria-label="Open menu"
          onClick={handleMobileMenuClick}
          css={css`
            padding: ${spacing.sm};
            color: ${theme.colors.text};
            cursor: pointer;
          `}
        >
          <Menu size={22} aria-hidden="true" />
        </button>
      </div>

      <main
        css={css`
          min-height: 0;
          min-width: 0;

          ${mq.lg} {
            grid-column: 2;
            grid-row: 1;
          }
        `}
      >
        <Outlet />
      </main>
    </div>
  );
}
